import { io, type Socket } from 'socket.io-client';
import { API_URL } from './api';

/**
 * The raffle's live channel (#92).
 *
 * The control panel is driven in front of an audience: the participant counter
 * has to move on its own while people scan the QR, and the panel must notice a
 * state change made from another device (a second organizer, the stage laptop)
 * without anyone pressing reload.
 *
 * **This is a nudge channel, never the source of truth.** Every handler below
 * re-reads the round over REST; the socket only says "something happened". That
 * is the backend's own rule for the Stage Screen (hard rule 7 of #89) and it
 * holds here for the same reason — a dropped frame during a live draw must not
 * leave the panel showing a stale status while the organizer presses buttons
 * against it.
 */

/**
 * The socket.io ORIGIN, which is not the REST base URL.
 *
 * `API_URL` points at the REST mount (`http://backend…/api`), but the gateway
 * is attached to the HTTP server itself and serves its handshake from the
 * default `/socket.io` path at the ORIGIN — it is not under `/api`. Handing the
 * `/api` URL to `io()` makes it request `/api/socket.io/…`, which Traefik
 * routes to the Nest HTTP router and answers with a 404 the client reports as
 * an opaque transport error. Strip the suffix instead of assuming either shape.
 */
const SOCKET_ORIGIN = API_URL.replace(/\/api\/?$/, '');

/** Mirrors `RAFFLE_SOCKET_NAMESPACE` in `raffles/raffle-public.types.ts`. */
const RAFFLE_NAMESPACE = '/raffle';

/** Mirrors `RAFFLE_EVENTS` in `raffles/raffle-public.types.ts`. */
export const RAFFLE_EVENTS = {
  STATE_CHANGED: 'state.changed',
  PARTICIPANT_JOINED: 'participant.joined',
  PRIZE_SHOWN: 'prize.shown',
  DRAW_STARTED: 'draw.started',
  WINNER_CONFIRMED: 'winner.confirmed',
} as const;

/** `participant.joined` — the counter and nothing else, ever. */
export interface RaffleParticipantJoinedPayload {
  count: number;
}

/** `state.changed` — only a nudge; the panel re-fetches the round. */
export interface RaffleStateChangedPayload {
  drawId: string;
  status: string;
  state: string;
}

/** `prize.shown` — which prize went up on the wall. */
export interface RafflePrizeShownPayload {
  drawId: string;
  prizeId: string;
}

/** `draw.started` / `winner.confirmed` — `playerName` is the PSEUDONYM. */
export interface RaffleWinnerEventPayload {
  drawId: string;
  prizeId: string;
  playerName: string;
}

/**
 * One shared connection for the whole panel.
 *
 * A module-level singleton rather than a socket per component: the control
 * panel, the winner card and the prize list all react to the same rooms, and a
 * connection per mounted component would multiply the handshakes and make the
 * room membership depend on mount order. Created lazily so that merely
 * importing this module (a test, a page that never opens a round) does not open
 * a connection.
 */
let socket: Socket | null = null;

export function getRaffleSocket(): Socket {
  if (!socket) {
    socket = io(`${SOCKET_ORIGIN}${RAFFLE_NAMESPACE}`, {
      // The gateway is anonymous by design (see the class comment on
      // `RaffleGateway`), so no auth payload is sent — everything it carries is
      // already served by the two public REST endpoints.
      //
      // Long-polling FIRST, websocket second — engine.io's own default order,
      // and the one the Stage Screen's `useRaffleSocket` documents and relies
      // on. Asking for websocket first saves a round trip and is silently fatal
      // behind a proxy that blocks the upgrade: engine.io-client 6.6.6 (pinned
      // through socket.io-client 4.8.3) only walks to the next entry of this
      // list on a connection error when `opts.tryAllTransports` is set, and
      // that option defaults to FALSE — so in `['websocket', 'polling']` the
      // second entry is dead code and the organizer gets no live updates at
      // all, with nothing on screen to say so. In this order the second entry
      // is the UPGRADE target instead (`upgrade` defaults to true), so a
      // blocked websocket costs the panel some latency rather than the channel.
      transports: ['polling', 'websocket'],
      autoConnect: true,
    });
  }
  return socket;
}

/**
 * Join the room of one round.
 *
 * Fire and forget: the server acknowledges nothing, and it drops a malformed
 * payload silently. Re-emitted on every `connect` by the caller's effect,
 * because a reconnect gives us a NEW socket id and socket.io does not restore
 * room membership on our behalf.
 *
 * **The only room this client ever joins.** The gateway broadcasts every event
 * to the draw room AND the event room (`broadcast` in `raffle.gateway.ts`), so
 * the event room carries nothing extra about THIS round — it only adds the
 * other rounds of the same conference. That is not free: `participant.joined`
 * is `{ count }` and nothing else (#89 point 3), with no `drawId` to filter on,
 * so a sibling round filling up would be read as this round's audience. There
 * is deliberately no `subscribeToEvent` here; a screen that wants every round
 * of a conference should re-read the list over REST.
 */
export function subscribeToDraw(drawId: string): void {
  getRaffleSocket().emit('subscribe.draw', { drawId });
}

/**
 * Drop the shared connection.
 *
 * Called when the last raffle screen unmounts. Without this the panel keeps an
 * open socket for the rest of the session, and every room it ever joined stays
 * joined — so a later round would receive the counter of an earlier one.
 */
export function closeRaffleSocket(): void {
  if (socket) {
    socket.removeAllListeners();
    socket.disconnect();
    socket = null;
  }
}
